/* Shared source viewer: one implementation for looking at a source document from anywhere
   (Dashboard and Sources today; Ask, Study Guide and Finder as their entry points move over).

   It renders its own copy of the document, so the workspace pane behind it keeps its
   document, page, zoom and highlights. Region selection is drawn here, and handed to the
   Ask bundle (window.MemoraSourceSelection) only once a box has actually been drawn, so
   plain viewing never loads AI or makes a model request. Quiz-only controls (Quiz up to)
   are not part of this viewer; they stay with the quiz workspace pane. */
(function initializeMemoraSourceViewer(global) {
    'use strict';

    const d = global.document;
    if (!d || global.MemoraSourceViewer?.version >= 1) return;

    const LAYER_ID = 'source-viewer';
    const ROOT_CLASS = 'memora-source-viewer';
    const BODY_OPEN_CLASS = 'memora-source-viewer-open';
    const ASK_PORTAL_ID = 'pdf-tutor-focus-ask-portal';
    // Fit is the floor: the file viewer never shrinks a page below the space it has.
    const ZOOM_STEPS = [1, 1.25, 1.5, 2, 2.5, 3];
    // Scroll distance per page turn at fit: about one mouse-wheel notch.
    const WHEEL_PAGE_DISTANCE = 100;
    const MAX_RENDER_WIDTH = 3200;
    const MAX_CROP_SOURCE_WIDTH = 4800;
    const THUMB_WIDTH = 320;
    const LONG_PRESS_MS = 380;
    const TOUCH_SLOP_PX = 8;
    const ASK_LOADING_NOTICE_DELAY_MS = 160;
    // A voice spotlight holds this long after it settles, then fades out on its own.
    const SPOTLIGHT_HOLD_MS = 1000;
    const SPOTLIGHT_FADE_MS = 350;

    // Successful positions survive closing/reopening in this tab, scoped to source revision and session.
    const lastViewedPages = new Map();

    let active = null;
    let sequence = 0;

    function sourceUrl(name) {
        try {
            return typeof loadedPdfFiles === 'undefined' ? '' : String(loadedPdfFiles?.[name] || '');
        } catch (_error) {
            return '';
        }
    }

    function isPptxName(name) {
        if (typeof global.isMemoraPptxSourceName === 'function') return !!global.isMemoraPptxSourceName(name);
        return /\.pptx?$/i.test(String(name || ''));
    }

    function normalizeCropRect(rect) {
        if (!rect || typeof rect !== 'object') return null;
        const x = Number(rect.x);
        const y = Number(rect.y);
        const width = Number(rect.width);
        const height = Number(rect.height);
        if (![x, y, width, height].every(Number.isFinite)) return null;
        if (width <= 0.01 || height <= 0.01 || x < 0 || y < 0 || x + width > 1.001 || y + height > 1.001) return null;
        if (width >= 0.995 && height >= 0.995) return null;
        return { x, y, width, height };
    }

    // Only http(s), blob and data:image sources are drawn; anything else is not an image to show.
    function normalizeImageUrl(url) {
        const value = String(url || '').trim();
        return /^(https?:|blob:|data:image\/)/i.test(value) ? value : '';
    }

    function normalizeAttribution(attribution) {
        const label = String(attribution?.label || '').trim().slice(0, 120);
        const href = /^https?:\/\//i.test(String(attribution?.href || '')) ? String(attribution.href) : '';
        return label || href ? { label: label || href.replace(/^https?:\/\/(www\.)?/i, '').split('/')[0], href } : null;
    }

    function pageMemoryKey(kind, name) {
        const session = typeof currentSessionId === 'undefined' ? '' : String(currentSessionId || '');
        const revision = typeof getMemoraSourceRevision === 'function' ? getMemoraSourceRevision(name)?.revision : '';
        return JSON.stringify([session, kind, name, String(revision || sourceUrl(name))]);
    }

    function rememberRenderedPage(view) {
        if (view.source.kind === 'image') return;
        const key = view.source.pageMemoryKey;
        lastViewedPages.delete(key);
        lastViewedPages.set(key, view.displayedPage);
        if (lastViewedPages.size > 100) lastViewedPages.delete(lastViewedPages.keys().next().value);
    }

    function publishViewerContext() {
        global.dispatchEvent(new global.CustomEvent('memora:source-viewer-change'));
    }

    function normalizeRequest(request = {}) {
        const source = request.source || {};
        const name = String(source.name || '').trim();
        const kind = source.kind === 'pptx' || source.kind === 'pdf' || source.kind === 'image'
            ? source.kind
            : (isPptxName(name) ? 'pptx' : 'pdf');
        const memoryKey = kind === 'image' ? '' : pageMemoryKey(kind, name);
        const requestedPage = Math.floor(Number(source.page));
        const page = requestedPage > 0 ? requestedPage : (lastViewedPages.get(memoryKey) || 1);
        return {
            source: {
                kind,
                // An image carries its own identity (its URL), never a lecture's name and page.
                name: kind === 'image' ? (name || 'Image') : name,
                url: kind === 'image' ? normalizeImageUrl(source.url) : '',
                reference: source.reference === true || request.origin?.surface === 'ask-reference',
                alt: String(source.alt || '').trim(),
                attribution: kind === 'image' ? normalizeAttribution(source.attribution) : null,
                page: kind === 'image' ? 1 : page,
                pageMemoryKey: memoryKey,
                cropRect: kind === 'image' ? null : normalizeCropRect(source.cropRect),
                title: String(source.title || '').trim()
            },
            origin: {
                surface: String(request.origin?.surface || '').trim(),
                opener: request.origin?.opener instanceof global.HTMLElement ? request.origin.opener : null
            },
            capabilities: {
                regionSelection: request.capabilities?.regionSelection !== false
            },
            // Owners that describe the screen elsewhere (the Voice Agent's screen context) follow
            // the viewer through these instead of reading its DOM.
            onPageChange: typeof request.onPageChange === 'function' ? request.onPageChange : null,
            onClose: typeof request.onClose === 'function' ? request.onClose : null
        };
    }

    function notifyOwner(view, hook, ...args) {
        try { view[hook]?.(...args); } catch (error) { console.warn('Source viewer owner callback failed:', error); }
    }

    // ---- Renderer adapters. Each owns only what it opened and releases it in destroy(). ----

    function drawImageUrlToCanvas(src, signal) {
        return new Promise((resolve, reject) => {
            if (!src) { reject(new Error('Nothing to draw.')); return; }
            const image = new global.Image();
            const cleanup = () => { image.onload = null; image.onerror = null; };
            image.onload = () => {
                cleanup();
                if (signal?.aborted) { resolve(null); return; }
                const canvas = d.createElement('canvas');
                canvas.width = Math.max(1, Number(image.naturalWidth) || 1);
                canvas.height = Math.max(1, Number(image.naturalHeight) || 1);
                const context = canvas.getContext('2d');
                if (!context) { reject(new Error('Canvas is unavailable.')); return; }
                context.drawImage(image, 0, 0);
                resolve(canvas);
            };
            image.onerror = () => { cleanup(); reject(new Error('Could not draw this slide.')); };
            image.src = src;
        });
    }

    async function createPdfAdapter(view) {
        const url = sourceUrl(view.source.name);
        if (!url) throw new Error('This source is not loaded in the current session.');
        await global.MemoraRuntime?.ensurePdfRuntimeLoaded?.();
        if (view.closed) return null;
        if (typeof getPdfJsDocumentWithReadbackCanvas !== 'function') throw new Error('The PDF renderer is not available.');
        const task = getPdfJsDocumentWithReadbackCanvas(url);
        view.release.push(() => { try { void Promise.resolve(task.destroy?.()).catch(() => {}); } catch (_error) { } });
        const pdf = await task.promise;
        if (view.closed) return null;
        const renderTasks = new Set();
        const renderPage = async (pageNumber, width) => {
            const page = await pdf.getPage(pageNumber);
            const base = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: Math.max(0.05, width / base.width) });
            const canvas = d.createElement('canvas');
            canvas.width = Math.max(1, Math.ceil(viewport.width));
            canvas.height = Math.max(1, Math.ceil(viewport.height));
            // Pixel rounding at different render widths must not change the page's fit geometry.
            canvas.msvAspect = base.width / base.height;
            const renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport });
            renderTasks.add(renderTask);
            try {
                await renderTask.promise;
            } finally {
                renderTasks.delete(renderTask);
            }
            return canvas;
        };
        view.release.push(() => renderTasks.forEach((renderTask) => { try { renderTask.cancel?.(); } catch (_error) { } }));
        return {
            count: Math.max(1, Number(pdf.numPages) || 1),
            unit: 'Page',
            readText: async (pageNumber) => {
                const page = await pdf.getPage(pageNumber);
                const content = await page.getTextContent();
                return content.items.map(item => item.str || '').join(' ').slice(0, 5000);
            },
            render: renderPage,
            thumbnail: (pageNumber) => renderPage(pageNumber, THUMB_WIDTH / 2)
        };
    }

    async function createPptxAdapter(view) {
        if (!sourceUrl(view.source.name)) throw new Error('This source is not loaded in the current session.');
        if (typeof global.getMemoraPptxSlideCount !== 'function' || typeof global.renderMemoraPptxSlideImage !== 'function') {
            throw new Error('A PowerPoint preview is not available yet.');
        }
        const count = Math.max(1, Number(await global.getMemoraPptxSlideCount(null, view.source.name)) || 1);
        if (view.closed) return null;
        const signal = view.abortController.signal;
        const render = async (pageNumber, width, quality = 0.86) => {
            const imageUrl = await global.renderMemoraPptxSlideImage(view.source.name, pageNumber, {
                maxWidth: Math.max(160, Math.round(width)),
                quality,
                abortSignal: signal
            });
            if (!imageUrl || signal.aborted) return null;
            return drawImageUrlToCanvas(imageUrl, signal);
        };
        return {
            count,
            unit: 'Slide',
            readText: async pageNumber => {
                const pages = await global.getMemoraPptxTextPages?.(view.source.name);
                return String(pages?.[pageNumber - 1]?.text || '').slice(0, 5000);
            },
            render,
            thumbnail: (pageNumber) => render(pageNumber, THUMB_WIDTH, 0.68)
        };
    }

    function loadImageElement(url, crossOrigin, signal) {
        return new Promise((resolve, reject) => {
            const image = new global.Image();
            const cleanup = () => { image.onload = null; image.onerror = null; signal?.removeEventListener('abort', abort); };
            const abort = () => { cleanup(); image.removeAttribute('src'); reject(new DOMException('Image view closed.', 'AbortError')); };
            if (signal?.aborted) { abort(); return; }
            signal?.addEventListener('abort', abort, { once: true });
            if (crossOrigin) image.crossOrigin = 'anonymous';
            image.referrerPolicy = 'no-referrer';
            image.decoding = 'async';
            image.onload = () => { cleanup(); resolve(image); };
            image.onerror = () => { cleanup(); image.removeAttribute('src'); reject(new Error('Could not load this image.')); };
            image.src = url;
        });
    }

    /* A standalone image: an answer's reference picture, an attachment, a quiz question's
       figure. Remote pictures are first requested with CORS so a highlighted area can be read
       back for Ask; a site that refuses still displays, it just cannot be sent. */
    // Display-time sharpening also works for cross-origin pictures without reading their pixels.
    function sharpenUpscaledImageCanvas(canvas) {
        const id = 'msv-enlarged-image-sharpen';
        if (!d.getElementById(id)) {
            const svg = d.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('aria-hidden', 'true');
            svg.style.cssText = 'position:absolute;width:0;height:0;pointer-events:none';
            svg.innerHTML = `<defs><filter id="${id}" color-interpolation-filters="sRGB"><feConvolveMatrix order="3" kernelMatrix="0 -0.1 0 -0.1 1.4 -0.1 0 -0.1 0" edgeMode="duplicate" preserveAlpha="true"/></filter></defs>`;
            d.body.appendChild(svg);
        }
        canvas.style.filter = `url(#${id})`;
    }

    async function createImageAdapter(view) {
        let url = view.source.url;
        if (!url) throw new Error('This image is not available.');
        if (view.source.reference && global.MemoraReferenceImageCache) {
            const lease = await global.MemoraReferenceImageCache.acquire(url, { signal: view.abortController.signal, maxDimension: 2048 });
            if (view.closed) { lease.release(); return null; }
            view.release.push(() => lease.release());
            url = lease.url;
        }
        const remote = /^https?:/i.test(url) && !url.startsWith(global.location?.origin || '\u0000');
        let image = null;
        let readable = true;
        try {
            image = await loadImageElement(url, remote, view.abortController.signal);
        } catch (error) {
            if (!remote || view.closed || error?.name === 'AbortError') throw error;
            image = await loadImageElement(url, false, view.abortController.signal);
            readable = false;
        }
        if (view.closed) { image.removeAttribute('src'); return null; }
        view.release.push(() => { image?.removeAttribute('src'); image = null; });
        view.imageElement = image;
        view.imagePixelsReadable = readable;
        const naturalWidth = Math.max(1, Number(image.naturalWidth) || 1);
        const naturalHeight = Math.max(1, Number(image.naturalHeight) || 1);
        return {
            count: 1,
            unit: 'Image',
            render: async (_pageNumber, width) => {
                if (view.closed || !image) return null;
                const scale = Math.max(1, width) / naturalWidth;
                const canvas = d.createElement('canvas');
                canvas.width = Math.max(1, Math.round(naturalWidth * scale));
                canvas.height = Math.max(1, Math.round(naturalHeight * scale));
                const context = canvas.getContext('2d');
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = 'high';
                context.drawImage(image, 0, 0, canvas.width, canvas.height);
                if (scale > 1.25) sharpenUpscaledImageCanvas(canvas);
                if (view.source.alt) canvas.setAttribute('aria-label', view.source.alt);
                canvas.setAttribute('role', 'img');
                return canvas;
            },
            thumbnail: async () => null
        };
    }

    // A cropped figure shows the crop the reader clicked. The full page is rendered at the
    // width that makes the crop the requested width, then the crop is cut out of it.
    async function renderCropped(adapter, pageNumber, width, cropRect) {
        const sourceWidth = Math.min(MAX_CROP_SOURCE_WIDTH, width / cropRect.width);
        const full = await adapter.render(pageNumber, sourceWidth);
        if (!full) return null;
        const sx = Math.floor(cropRect.x * full.width);
        const sy = Math.floor(cropRect.y * full.height);
        const sw = Math.max(1, Math.min(full.width - sx, Math.ceil(cropRect.width * full.width)));
        const sh = Math.max(1, Math.min(full.height - sy, Math.ceil(cropRect.height * full.height)));
        const canvas = d.createElement('canvas');
        canvas.width = sw;
        canvas.height = sh;
        canvas.getContext('2d')?.drawImage(full, sx, sy, sw, sh, 0, 0, sw, sh);
        full.width = 0;
        full.height = 0;
        return canvas;
    }

    // ---- DOM ----

    function buildRoot(view) {
        const root = d.createElement('div');
        root.id = 'memora-source-viewer';
        root.className = ROOT_CLASS;
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-modal', 'true');
        root.tabIndex = -1;
        root.setAttribute('aria-label', 'Source: ' + view.source.name);
        root.dataset.sourceKind = view.source.kind;
        if (view.origin.surface) root.dataset.originSurface = view.origin.surface;
        root.innerHTML = [
            '<div class="msv-backdrop" data-msv-dismiss></div>',
            '<section class="msv-panel">',
            '<section class="msv-pane" aria-label="First source">',
            /* Identity on the left (pages toggle, title), page and zoom controls as two
               segmented pills, then Highlights (only once there is one) and Close. */
            '<header class="msv-toolbar">',
            '<button type="button" class="msv-btn msv-icon msv-thumbs-toggle" data-msv-thumbs aria-expanded="true" aria-controls="memora-source-viewer-thumbs" aria-label="Hide pages" title="Hide pages">',
            '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><rect x="2.5" y="3.5" width="15" height="13" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.6"/><path class="msv-thumbs-icon-strip" d="M3.3 4.3h4.2v11.4H3.3z" fill="currentColor"/><path d="M7.5 3.8v12.4" stroke="currentColor" stroke-width="1.6"/><path class="msv-thumbs-icon-chevron" d="M13.9 7.2L11.1 10l2.8 2.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
            '</button>',
            '<div class="msv-identity">',
            '<strong class="msv-title"></strong>',
            '</div>',
            '<span class="msv-voice-slot" aria-hidden="true"></span>',
            '<button type="button" class="msv-btn msv-pull-slide" data-msv-pull-slide aria-haspopup="menu" aria-expanded="false" aria-label="Pull Up Another Slide" title="Pull Up Another Slide"><span class="msv-pull-slide-label">Pull Up Another Slide</span><svg class="msv-pull-slide-icon" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><rect x="2" y="4" width="7" height="12" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="11" y="4" width="7" height="12" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button>',
            '<div class="msv-group msv-nav msv-segment">',
            '<button type="button" class="msv-btn msv-icon" data-msv-prev aria-label="Previous page">&#8249;</button>',
            '<span class="msv-position" aria-live="polite"></span>',
            '<button type="button" class="msv-btn msv-icon" data-msv-next aria-label="Next page">&#8250;</button>',
            '</div>',
            '<div class="msv-group msv-zoom msv-segment">',
            '<button type="button" class="msv-btn msv-icon" data-msv-zoom-out aria-label="Zoom out">&minus;</button>',
            '<button type="button" class="msv-btn msv-zoom-level" data-msv-fit aria-label="Fit to window">Fit</button>',
            '<button type="button" class="msv-btn msv-icon" data-msv-zoom-in aria-label="Zoom in">+</button>',
            '</div>',
            '<button type="button" class="msv-btn msv-highlights" data-msv-highlights data-state="hidden" aria-pressed="false" hidden>',
            '<svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" focusable="false"><path d="M12.6 3.4l4 4-7.9 7.9H4.7v-4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M3 17.5h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
            '<span class="msv-highlights-label">Show highlights</span>',
            '</button>',
            '<button type="button" class="msv-btn msv-icon msv-close" data-msv-close aria-label="Close" title="Close">',
            '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
            '</button>',
            '</header>',
            '<div class="msv-body">',
            // A floating pill over the page, so a message never pushes the viewer down.
            '<p class="msv-notice" role="status" aria-live="polite" hidden></p>',
            '<nav id="memora-source-viewer-thumbs" class="msv-thumbs" aria-label="Pages"></nav>',
            '<div class="msv-stage">',
            '<p class="msv-status" role="status">Loading preview…</p>',
            '<div class="msv-page" hidden>',
            '<div class="msv-canvas-slot"></div>',
            '<div class="pdf-tutor-focus-layer pdf-tutor-slide-finder-zoom-layer msv-layer" aria-hidden="false"></div>',
            '</div>',
            '</div>',
            '</div>',
            '</section>',
            '</section>'
        ].join('');
        const thumbsId = 'memora-source-viewer-thumbs-' + view.id;
        root.querySelector('.msv-thumbs').id = thumbsId;
        root.querySelector('[data-msv-thumbs]').setAttribute('aria-controls', thumbsId);
        root.querySelector('.msv-title').textContent = view.source.title || view.source.name;
        root.querySelector('.msv-title').title = view.source.title || view.source.name;
        if (view.source.kind === 'image') {
            // One picture: no pages to step through, and its highlights are not saved.
            root.querySelector('.msv-nav').hidden = true;
            const attribution = view.source.attribution;
            if (attribution) {
                const credit = d.createElement(attribution.href ? 'a' : 'span');
                credit.className = 'msv-attribution';
                credit.textContent = attribution.label;
                if (attribution.href) {
                    credit.href = attribution.href;
                    credit.target = '_blank';
                    credit.rel = 'noopener noreferrer';
                    credit.title = 'Open the original';
                }
                root.querySelector('.msv-title').after(credit);
            }
        }
        return root;
    }

    function el(view, selector) {
        return (view.pane || view.root).querySelector(selector);
    }

    const NOTICE_FADE_MS = 180;
    const NOTICE_HINT_MS = 4500;

    /* The notice fades and slides in over the page and fades out before it hides. A hint
       (autoHide) dismisses itself; errors and progress stay until they are replaced or cleared. */
    function setNotice(view, message, { autoHide = false } = {}) {
        const notice = el(view, '.msv-notice');
        if (!notice) return;
        global.clearTimeout(view.noticeHideTimer);
        global.clearTimeout(view.noticeFadeTimer);
        if (!message) {
            notice.classList.remove('is-visible');
            view.noticeFadeTimer = global.setTimeout(() => {
                if (notice.classList.contains('is-visible')) return;
                notice.hidden = true;
                notice.textContent = '';
            }, NOTICE_FADE_MS);
            return;
        }
        notice.textContent = String(message);
        const wasHidden = notice.hidden;
        notice.hidden = false;
        if (wasHidden) void notice.offsetWidth;
        notice.classList.add('is-visible');
        if (autoHide) view.noticeHideTimer = global.setTimeout(() => { if (isCurrent(view)) setNotice(view, ''); }, NOTICE_HINT_MS);
    }

    function setStatus(view, message) {
        const status = el(view, '.msv-status');
        if (!status) return;
        status.textContent = String(message || '');
        status.hidden = !message;
    }

    function isCurrent(view) {
        return !!view && !view.closed && (active === view || active?.comparison === view);
    }

    // ---- Layout and zoom ----

    function fitWidth(view) {
        const stage = el(view, '.msv-stage');
        const aspect = view.aspect || (4 / 3);
        const availableWidth = Math.max(120, (stage?.clientWidth || 800) - 32);
        const availableHeight = Math.max(120, (stage?.clientHeight || 600) - 32);
        // Small sources still fill useful space, but past 2x the sharpened upscale turns to mush.
        const imageWidth = view.source.kind === 'image' && view.imageElement?.naturalWidth
            ? view.imageElement.naturalWidth * 2 : Infinity;
        return Math.max(1, Math.min(availableWidth, availableHeight * aspect, imageWidth));
    }

    function displayWidth(view) {
        return Math.round(fitWidth(view) * view.zoom);
    }

    /* The Voice orb floats at the top of the window and landed half over the panel's top edge.
       While a viewer is open it sits in the toolbar slot reserved for it; the slot only has
       width while Voice is connected, and the rail CSS places the orb on these coordinates. */
    function seatVoiceOrb(view) {
        if (view.parent || !isCurrent(view)) return;
        const rect = view.root?.querySelector('.msv-voice-slot')?.getBoundingClientRect();
        if (!rect?.width) {
            delete d.body.dataset.msvVoiceSeat;
            return;
        }
        d.body.style.setProperty('--msv-voice-slot-x', (rect.left + rect.width / 2) + 'px');
        d.body.style.setProperty('--msv-voice-slot-y', (rect.top + rect.height / 2) + 'px');
        d.body.dataset.msvVoiceSeat = 'toolbar';
    }

    function applyLayout(view) {
        const pageEl = el(view, '.msv-page');
        if (!pageEl) return;
        pageEl.style.width = displayWidth(view) + 'px';
        pageEl.style.aspectRatio = String(view.aspect || (4 / 3));
        const level = el(view, '[data-msv-fit]');
        if (level) level.textContent = view.zoom === 1 ? 'Fit' : Math.round(view.zoom * 100) + '%';
        el(view, '[data-msv-zoom-out]').disabled = view.zoom <= ZOOM_STEPS[0];
        el(view, '[data-msv-zoom-in]').disabled = view.zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1];
    }

    function wantedRenderWidth(view) {
        const ratio = Math.max(1, Math.min(2.5, Number(global.devicePixelRatio) || 1));
        return Math.min(MAX_RENDER_WIDTH, Math.round(displayWidth(view) * ratio));
    }

    function scheduleSharpen(view) {
        global.clearTimeout(view.sharpenTimer);
        view.sharpenTimer = global.setTimeout(() => {
            if (!isCurrent(view)) return;
            if (view.rendering) {
                view.sharpenAfterRender = true;
                return;
            }
            view.sharpenAfterRender = false;
            if (wantedRenderWidth(view) > view.renderedWidth + 2) void renderPage(view);
        }, 140);
    }

    function setZoom(view, zoom, clientX = null, clientY = null) {
        const next = Math.max(ZOOM_STEPS[0], Math.min(ZOOM_STEPS[ZOOM_STEPS.length - 1], zoom));
        if (!Number.isFinite(next)) return;
        const stage = el(view, '.msv-stage');
        const pageEl = el(view, '.msv-page');
        const resetFit = next === 1 && clientX === null && clientY === null;
        if (next === view.zoom && !resetFit) return;
        const stageRect = stage.getBoundingClientRect();
        const before = pageEl.getBoundingClientRect();
        const x = clientX ?? (stageRect.left + stage.clientWidth / 2);
        const y = clientY ?? (stageRect.top + stage.clientHeight / 2);
        const previousAnchor = view.zoomAnchor;
        const keepAnchor = clientX !== null && clientY !== null && previousAnchor
            && previousAnchor.page === view.page
            && previousAnchor.scrollLeft === stage.scrollLeft && previousAnchor.scrollTop === stage.scrollTop
            && previousAnchor.stageWidth === stage.clientWidth && previousAnchor.stageHeight === stage.clientHeight
            && Math.abs(previousAnchor.x - x) < 1 && Math.abs(previousAnchor.y - y) < 1;
        const ratioX = keepAnchor ? previousAnchor.ratioX : Math.max(0, Math.min(1, (x - before.left) / Math.max(1, before.width)));
        const ratioY = keepAnchor ? previousAnchor.ratioY : Math.max(0, Math.min(1, (y - before.top) / Math.max(1, before.height)));
        // Keep the same source point for a whole pinch instead of accumulating the
        // browser's fractional scroll rounding on every high-frequency wheel event.
        view.zoomAnchor = clientX !== null && clientY !== null ? { x, y, ratioX, ratioY, page: view.page } : null;
        const previousZoom = view.zoom;
        view.zoom = next;
        applyLayout(view);
        if (resetFit) {
            pageEl.style.margin = '';
            stage.scrollLeft = stage.scrollTop = 0;
        } else {
            const after = pageEl.getBoundingClientRect();
            const style = global.getComputedStyle(stage);
            const padLeft = parseFloat(style.paddingLeft) || 0;
            const padRight = parseFloat(style.paddingRight) || 0;
            const padTop = parseFloat(style.paddingTop) || 0;
            const padBottom = parseFloat(style.paddingBottom) || 0;
            let left = x - stageRect.left - padLeft - ratioX * after.width;
            let top = y - stageRect.top - padTop - ratioY * after.height;
            /* Panning a zoomed page moves the anchor off-center, so zooming back out would
               land the page off-center. While zooming out, the page's offset from center
               shrinks in step with the zoom, so it glides back and is centered at Fit. */
            if (next < previousZoom && previousZoom > 1) {
                const keep = Math.max(0, (next - 1) / (previousZoom - 1));
                const recenter = (pos, size, previousPos, previousSize, room) => {
                    const center = (room - size) / 2;
                    let limit = Math.abs(previousPos - (room - previousSize) / 2) * keep;
                    // An overflowing page still covers the viewport edge to edge.
                    if (size > room) limit = Math.min(limit, (size - room) / 2);
                    return center + Math.max(-limit, Math.min(limit, pos - center));
                };
                left = recenter(left, after.width, before.left - stageRect.left - padLeft, before.width, stage.clientWidth - padLeft - padRight);
                top = recenter(top, after.height, before.top - stageRect.top - padTop, before.height, stage.clientHeight - padTop - padBottom);
            }
            // Explicit space around the page lets scrolling anchor a pinch even while
            // the page is smaller than the viewport. Auto margins would recenter it
            // until Fit, then abruptly switch to off-center anchoring at overflow.
            pageEl.style.marginLeft = Math.max(0, left) + 'px';
            pageEl.style.marginTop = Math.max(0, top) + 'px';
            pageEl.style.marginRight = Math.max(0, stage.clientWidth - padLeft - padRight - after.width - left) + 'px';
            pageEl.style.marginBottom = Math.max(0, stage.clientHeight - padTop - padBottom - after.height - top) + 'px';
            stage.scrollLeft = Math.max(0, -left);
            stage.scrollTop = Math.max(0, -top);
        }
        if (view.zoomAnchor) Object.assign(view.zoomAnchor, {
            scrollLeft: stage.scrollLeft, scrollTop: stage.scrollTop,
            stageWidth: stage.clientWidth, stageHeight: stage.clientHeight
        });
        scheduleSharpen(view);
    }

    function bindZoomGestures(view) {
        const stage = el(view, '.msv-stage');
        let wheelTime = 0;
        let wheelDelta = 0;
        let gestureZoom = null;
        // Zoom only answers a pinch over the slide itself, not the margin around it.
        const overPage = (event) => {
            const box = el(view, '.msv-page')?.getBoundingClientRect();
            return !!box && event.clientX >= box.left && event.clientX <= box.right
                && event.clientY >= box.top && event.clientY <= box.bottom;
        };
        stage.addEventListener('wheel', (event) => {
            if (!isCurrent(view) || !view.adapter) return;
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
                if (!overPage(event)) return;
                cancelDrawing(view);
                // Use the workspace viewer's bounded touchpad/wheel sensitivity.
                setZoom(view, view.zoom * getPdfWheelZoomFactor(event.deltaY, event.deltaMode), event.clientX, event.clientY);
                return;
            }
            // A zoomed page keeps native two-axis panning. At fit, scroll turns pages.
            if (view.zoom > 1 || view.adapter.count < 2 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
            event.preventDefault();
            const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stage.clientHeight : 1);
            if (!delta) return;
            const now = event.timeStamp;
            if (now - wheelTime > 180 || Math.sign(delta) !== Math.sign(wheelDelta)) wheelDelta = 0;
            wheelTime = now;
            wheelDelta += delta;
            // Distance turns pages: a longer swipe, or its inertial tail, keeps turning them.
            const steps = Math.trunc(wheelDelta / WHEEL_PAGE_DISTANCE);
            if (!steps) return;
            wheelDelta -= steps * WHEEL_PAGE_DISTANCE;
            void goToPage(view, view.page + steps);
        }, { passive: false });
        // Safari touchpads report GestureEvents instead of ctrl+wheel.
        stage.addEventListener('gesturestart', (event) => {
            event.preventDefault();
            if (!overPage(event)) return;
            cancelDrawing(view);
            gestureZoom = view.zoom;
        }, { passive: false });
        stage.addEventListener('gesturechange', (event) => {
            if (gestureZoom === null) return;
            event.preventDefault();
            setZoom(view, gestureZoom * event.scale, event.clientX, event.clientY);
        }, { passive: false });
        stage.addEventListener('gestureend', (event) => {
            event.preventDefault();
            gestureZoom = null;
        }, { passive: false });
    }

    function stepZoom(view, direction) {
        const current = view.zoom;
        const next = direction > 0
            ? ZOOM_STEPS.find((step) => step > current + 0.001)
            : [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001);
        if (next) setZoom(view, next);
    }

    // ---- Paging ----

    function updateChrome(view) {
        if (!isCurrent(view) || !view.adapter) return;
        const count = view.adapter.count;
        const position = el(view, '.msv-position');
        const pageLabel = view.adapter.unit + ' ' + view.page + ' of ' + count;
        position.textContent = view.parent || view.comparison ? view.page + ' / ' + count : pageLabel;
        position.setAttribute('aria-label', pageLabel);
        position.title = pageLabel;
        const previous = el(view, '[data-msv-prev]');
        const next = el(view, '[data-msv-next]');
        previous.setAttribute('aria-label', 'Previous ' + view.adapter.unit.toLowerCase());
        next.setAttribute('aria-label', 'Next ' + view.adapter.unit.toLowerCase());
        /* Reaching the first or last page disables the button that got there. Hand focus to
           its partner so a keyboard user is not dropped onto the page behind the viewer. */
        const focused = d.activeElement;
        previous.disabled = view.page <= 1;
        next.disabled = view.page >= count;
        if ((focused === previous || focused === next) && focused.disabled) {
            const partner = focused === next ? previous : next;
            (partner.disabled ? el(view, '[data-msv-close]') : partner)?.focus({ preventScroll: true });
        }
        const thumbs = el(view, '.msv-thumbs');
        thumbs?.querySelectorAll('button[data-msv-page]').forEach((button) => {
            const selected = Number(button.dataset.msvPage) === view.page;
            button.setAttribute('aria-current', selected ? 'page' : 'false');
        });
        view.thumbsUserScrolled = false;
        revealCurrentThumb(view);
    }

    /* Keeps the open page in view inside the page strip. It scrolls the strip alone (never the
       page behind the viewer) and treats the faded bottom edge as out of view. */
    function revealCurrentThumb(view) {
        const thumbs = el(view, '.msv-thumbs');
        if (!thumbs || thumbs.hidden) return;
        const button = thumbs.querySelector('button[aria-current="page"]');
        if (!button) return;
        const strip = thumbs.getBoundingClientRect();
        const rect = button.getBoundingClientRect();
        const fade = 64;
        if (rect.top >= strip.top + 8 && rect.bottom <= strip.bottom - fade) return;
        const offset = rect.top - strip.top + thumbs.scrollTop;
        thumbs.scrollTop = Math.max(0, offset - (strip.height - rect.height) / 2);
    }

    function syncHostIdentity(view) {
        const pageEl = el(view, '.msv-page');
        if (!pageEl) return;
        pageEl.dataset.pdfName = view.source.name;
        pageEl.dataset.pageNumber = String(view.page);
        pageEl.dataset.slideTitle = view.adapter ? view.adapter.unit + ' ' + view.page : '';
        if (view.cropRect) pageEl.dataset.sourceCropRect = JSON.stringify(view.cropRect);
        else delete pageEl.dataset.sourceCropRect;
    }

    async function renderPage(view) {
        if (!isCurrent(view) || !view.adapter) return false;
        const pageNumber = view.page;
        const token = ++view.renderToken;
        const width = wantedRenderWidth(view);
        view.rendering = true;
        try {
            const canvas = view.cropRect
                ? await renderCropped(view.adapter, pageNumber, width, view.cropRect)
                : await view.adapter.render(pageNumber, width);
            if (!isCurrent(view) || token !== view.renderToken || !canvas) {
                if (canvas) { canvas.width = 0; canvas.height = 0; }
                return false;
            }
            canvas.className = 'msv-canvas';
            const slot = el(view, '.msv-canvas-slot');
            const previous = slot.querySelector('canvas');
            slot.replaceChildren(canvas);
            if (previous) { previous.width = 0; previous.height = 0; }
            const aspect = canvas.msvAspect || canvas.width / Math.max(1, canvas.height);
            const aspectChanged = Math.abs(aspect - (view.aspect || 0)) > 0.001;
            view.aspect = aspect;
            view.renderedWidth = canvas.width;
            view.displayedPage = pageNumber;
            rememberRenderedPage(view);
            publishViewerContext();
            el(view, '.msv-page').hidden = false;
            setStatus(view, '');
            if (aspectChanged) {
                applyLayout(view);
                scheduleSharpen(view);
            }
            return true;
        } catch (error) {
            if (isCurrent(view) && token === view.renderToken && error?.name !== 'RenderingCancelledException') {
                setStatus(view, 'Could not render this ' + (view.adapter.unit || 'page').toLowerCase() + '.');
            }
            return false;
        } finally {
            if (token === view.renderToken) {
                view.rendering = false;
                // Zoom/resize may settle while this render is busy. Keep its final sharp render.
                if (view.sharpenAfterRender && isCurrent(view)) {
                    view.sharpenAfterRender = false;
                    scheduleSharpen(view);
                }
            }
        }
    }

    async function goToPage(view, pageNumber) {
        if (!isCurrent(view) || !view.adapter) return;
        const next = Math.max(1, Math.min(view.adapter.count, Math.floor(Number(pageNumber) || 1)));
        if (next === view.page && view.renderedWidth) return;
        cancelDrawing(view);
        clearSpotlight(view);
        closeHighlightsMenu(view);
        global.MemoraSourceSelection?.closeAskCard?.(view.root);
        global.MemoraSourceSelection?.clearAll?.(el(view, '.msv-page'));
        view.page = next;
        // A crop belongs to the page it was cut from; any other page shows whole.
        view.cropRect = next === view.source.page ? view.source.cropRect : null;
        syncHostIdentity(view);
        updateChrome(view);
        notifyOwner(view, 'onPageChange', next);
        const rendered = await renderPage(view);
        if (rendered) void refreshHighlights(view, { render: true });
    }

    // ---- Page thumbnails ----

    function buildThumbnails(view) {
        const thumbs = el(view, '.msv-thumbs');
        const toggle = el(view, '[data-msv-thumbs]');
        const count = view.adapter.count;
        if (count < 2) {
            thumbs.hidden = true;
            toggle.hidden = true;
            view.pane.classList.add('msv-single-page');
            return;
        }
        // Narrow screens start with the page strip closed so the page gets the width.
        if ((global.innerWidth || 1024) < 760) setThumbsOpen(view, false);
        let queue = Promise.resolve();
        const paint = (button) => {
            queue = queue.catch(() => {}).then(async () => {
                if (!isCurrent(view)) return;
                const canvas = await view.adapter.thumbnail(Number(button.dataset.msvPage));
                if (!isCurrent(view) || !canvas) {
                    if (canvas) { canvas.width = 0; canvas.height = 0; }
                    return;
                }
                canvas.className = 'msv-thumb-canvas';
                canvas.setAttribute('aria-hidden', 'true');
                button.prepend(canvas);
                // A painted page is taller than its placeholder and can push the open page out of
                // view, so follow it until the reader scrolls the strip themselves.
                if (!view.thumbsUserScrolled) revealCurrentThumb(view);
            });
        };
        const markUserScrolled = () => { view.thumbsUserScrolled = true; };
        ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => {
            thumbs.addEventListener(type, markUserScrolled, { passive: true });
        });
        if (global.IntersectionObserver) {
            view.thumbObserver = new global.IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    view.thumbObserver.unobserve(entry.target);
                    paint(entry.target);
                });
            }, { root: thumbs, rootMargin: '160px' });
        }
        const fragment = d.createDocumentFragment();
        for (let number = 1; number <= count; number++) {
            const button = d.createElement('button');
            button.type = 'button';
            button.className = 'msv-thumb';
            button.dataset.msvPage = String(number);
            button.setAttribute('aria-label', view.adapter.unit + ' ' + number);
            const label = d.createElement('span');
            label.textContent = String(number);
            button.append(label);
            fragment.append(button);
        }
        thumbs.append(fragment);
        thumbs.querySelectorAll('button[data-msv-page]').forEach((button) => {
            if (view.thumbObserver) view.thumbObserver.observe(button);
            else paint(button);
        });
    }

    function setThumbsOpen(view, open) {
        const thumbs = el(view, '.msv-thumbs');
        const toggle = el(view, '[data-msv-thumbs]');
        thumbs.hidden = !open;
        view.pane.classList.toggle('msv-thumbs-closed', !open);
        toggle.setAttribute('aria-expanded', String(open));
        // The toggle is an icon; its name says what a press will do.
        const label = open ? 'Hide pages' : 'Show pages';
        toggle.setAttribute('aria-label', label);
        toggle.title = label;
        global.requestAnimationFrame?.(() => {
            if (!isCurrent(view)) return;
            applyLayout(view);
            if (open) revealCurrentThumb(view);
        });
    }

    // ---- Another slide: sources -> slides -> independent comparison pane ----

    const SLIDE_PREVIEW_DELAY_MS = 350;

    function disposePickerSource(picker) {
        const resource = picker.resource;
        picker.resource = null;
        if (!resource) return;
        resource.closed = true;
        resource.abortController.abort();
        resource.release.splice(0).forEach((release) => { try { release(); } catch (_error) { } });
    }

    function clearSlidePreview(picker) {
        global.clearTimeout(picker.previewTimer);
        picker.previewToken = (picker.previewToken || 0) + 1;
        picker.preview.hidden = true;
        picker.preview.querySelectorAll('canvas').forEach((canvas) => { canvas.width = 0; canvas.height = 0; });
        picker.preview.replaceChildren();
    }

    function closeSourcePicker(view, { restoreFocus = false } = {}) {
        const picker = view?.sourcePicker;
        if (!picker) return false;
        view.sourcePicker = null;
        clearSlidePreview(picker);
        disposePickerSource(picker);
        picker.abortController.abort();
        picker.root.remove();
        const button = el(view, '[data-msv-pull-slide]');
        button?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) button?.focus({ preventScroll: true });
        return true;
    }

    function scheduleSlidePreview(view, picker, row, pageNumber) {
        clearSlidePreview(picker);
        const token = picker.previewToken;
        picker.previewTimer = global.setTimeout(async () => {
            if (view.sourcePicker !== picker || !picker.adapter || token !== picker.previewToken) return;
            const preview = picker.preview;
            const place = () => {
                const rect = row.getBoundingClientRect();
                preview.style.top = Math.max(8, Math.min(rect.top, global.innerHeight - preview.offsetHeight - 8)) + 'px';
            };
            preview.hidden = false;
            preview.textContent = 'Loading Slide ' + pageNumber + '…';
            place();
            try {
                const canvas = await picker.adapter.render(pageNumber, 880);
                if (!canvas || view.sourcePicker !== picker || token !== picker.previewToken) {
                    if (canvas) { canvas.width = 0; canvas.height = 0; }
                    return;
                }
                canvas.setAttribute('role', 'img');
                canvas.setAttribute('aria-label', picker.name + ', Slide ' + pageNumber);
                const caption = d.createElement('span');
                caption.textContent = 'Slide ' + pageNumber;
                preview.replaceChildren(canvas, caption);
                place();
            } catch (_error) {
                if (view.sourcePicker === picker && token === picker.previewToken) preview.textContent = 'Preview unavailable. Select the slide to open it.';
            }
        }, SLIDE_PREVIEW_DELAY_MS);
    }

    async function choosePickerSource(view, picker, name) {
        clearSlidePreview(picker);
        disposePickerSource(picker);
        picker.adapter = null;
        picker.name = name;
        picker.title.textContent = name;
        picker.list.textContent = 'Loading slides…';
        picker.track.classList.add('is-selecting-slides');
        picker.back.focus({ preventScroll: true });
        const resource = { source: { name }, release: [], closed: false, abortController: new AbortController() };
        picker.resource = resource;
        try {
            const borrowed = [view, view.comparison].find((pane) => pane?.source.name === name && pane.adapter && !pane.closed);
            picker.borrowedView = borrowed || null;
            const adapter = borrowed?.adapter
                || await (isPptxName(name) ? createPptxAdapter(resource) : createPdfAdapter(resource));
            if (!adapter || view.sourcePicker !== picker || picker.resource !== resource) return;
            picker.adapter = adapter;
            picker.list.replaceChildren();
            const fragment = d.createDocumentFragment();
            for (let pageNumber = 1; pageNumber <= adapter.count; pageNumber++) {
                const row = d.createElement('button');
                row.type = 'button';
                row.className = 'msv-picker-option';
                row.setAttribute('role', 'menuitem');
                row.textContent = 'Slide ' + pageNumber;
                row.addEventListener('pointerenter', (event) => {
                    if (event.pointerType !== 'touch') scheduleSlidePreview(view, picker, row, pageNumber);
                });
                row.addEventListener('pointerleave', () => clearSlidePreview(picker));
                row.addEventListener('focus', () => scheduleSlidePreview(view, picker, row, pageNumber));
                row.addEventListener('blur', () => clearSlidePreview(picker));
                row.addEventListener('click', () => openComparison(view, picker, pageNumber));
                fragment.append(row);
            }
            picker.list.append(fragment);
        } catch (error) {
            if (view.sourcePicker === picker && picker.resource === resource) picker.list.textContent = error?.message || 'Could not load these slides. Choose another source.';
        }
    }

    function openSourcePicker(view) {
        if (!isCurrent(view) || view.parent) return;
        closeHighlightsMenu(view);
        if (view.comparison) closeHighlightsMenu(view.comparison);
        const root = d.createElement('div');
        root.className = 'msv-source-picker';
        root.innerHTML = '<div class="msv-picker-window"><div class="msv-picker-track"><section class="msv-picker-sources"><strong>Choose a source</strong><div class="msv-picker-source-list" role="menu" aria-label="Sources"></div></section><section class="msv-picker-slides" inert><header><button type="button" class="msv-picker-back" aria-label="Back to sources">&#8249;</button><strong></strong></header><div class="msv-picker-slide-list" role="menu" aria-label="Slides"></div></section></div></div><aside class="msv-picker-preview" aria-live="polite" hidden></aside>';
        const picker = {
            root, track: root.querySelector('.msv-picker-track'),
            title: root.querySelector('.msv-picker-slides strong'),
            back: root.querySelector('.msv-picker-back'),
            list: root.querySelector('.msv-picker-slide-list'),
            preview: root.querySelector('.msv-picker-preview'),
            abortController: new AbortController(), previewToken: 0
        };
        const sourcesPanel = root.querySelector('.msv-picker-sources');
        const slidesPanel = root.querySelector('.msv-picker-slides');
        const sources = root.querySelector('.msv-picker-source-list');
        const names = typeof loadedPdfFiles === 'undefined' ? [] : Object.keys(loadedPdfFiles).filter((name) => sourceUrl(name));
        view.sourcePicker = picker;
        for (const name of names) {
            const row = d.createElement('button');
            row.type = 'button';
            row.className = 'msv-picker-option';
            row.setAttribute('role', 'menuitem');
            row.textContent = name;
            row.title = name;
            row.addEventListener('click', () => {
                picker.sourceRow = row;
                sourcesPanel.inert = true;
                slidesPanel.inert = false;
                void choosePickerSource(view, picker, name);
            });
            sources.append(row);
        }
        if (!names.length) sources.textContent = 'Upload a PDF or PowerPoint to compare slides.';
        picker.back.addEventListener('click', () => {
            clearSlidePreview(picker);
            disposePickerSource(picker);
            picker.track.classList.remove('is-selecting-slides');
            slidesPanel.inert = true;
            sourcesPanel.inert = false;
            picker.sourceRow?.focus({ preventScroll: true });
        });
        root.addEventListener('keydown', (event) => {
            if (event.key === 'Tab') { closeSourcePicker(view); return; }
            const panel = picker.track.classList.contains('is-selecting-slides') ? slidesPanel : sourcesPanel;
            const items = Array.from(panel.querySelectorAll('button:not(:disabled)'));
            const index = items.indexOf(d.activeElement);
            let next = null;
            if (event.key === 'ArrowDown') next = items[(index + 1) % items.length];
            if (event.key === 'ArrowUp') next = items[(index - 1 + items.length) % items.length];
            if (event.key === 'Home') next = items[0];
            if (event.key === 'End') next = items[items.length - 1];
            if (event.key === 'ArrowLeft' && panel === slidesPanel) { event.preventDefault(); picker.back.click(); }
            if (next) { event.preventDefault(); next.focus(); }
        });
        picker.list.addEventListener('scroll', () => clearSlidePreview(picker), { passive: true });
        view.root.append(root);
        const rect = el(view, '[data-msv-pull-slide]').getBoundingClientRect();
        const width = 272;
        const previewWidth = Math.min(440, Math.max(220, global.innerWidth - width - 34));
        const left = Math.max(8, Math.min(rect.left, global.innerWidth - width - previewWidth - 26));
        const top = Math.min(rect.bottom + 6, global.innerHeight - 160);
        root.style.left = left + 'px';
        root.style.top = Math.max(8, top) + 'px';
        root.style.setProperty('--msv-picker-height', Math.min(380, global.innerHeight - top - 12) + 'px');
        picker.preview.style.left = left + width + 10 + 'px';
        picker.preview.style.width = previewWidth + 'px';
        el(view, '[data-msv-pull-slide]').setAttribute('aria-expanded', 'true');
        sources.querySelector('button')?.focus({ preventScroll: true });
        global.addEventListener('resize', () => closeSourcePicker(view), { signal: picker.abortController.signal });
    }

    function openComparison(view, picker, pageNumber) {
        if (!isCurrent(view) || view.sourcePicker !== picker || !picker.adapter) return;
        if (picker.borrowedView === view.comparison && view.comparison) {
            closeSourcePicker(view);
            setThumbsOpen(view, false);
            setThumbsOpen(view.comparison, true);
            void goToPage(view.comparison, pageNumber);
            el(view.comparison, '[data-msv-close]').focus({ preventScroll: true });
            return;
        }
        const comparison = createView(normalizeRequest({ source: { name: picker.name, page: pageNumber } }));
        comparison.parent = view;
        comparison.adapter = picker.adapter;
        // Transfer the picker's document lease to the pane; the original source stays loaded.
        comparison.release.push(...picker.resource.release.splice(0));
        comparison.abortController = picker.resource.abortController;
        picker.resource = null;
        closeSourcePicker(view);
        if (view.comparison) teardown(view.comparison);
        else view.thumbsBeforeComparison = !el(view, '.msv-thumbs').hidden;
        view.comparison = comparison;
        const wrapper = buildRoot(comparison);
        comparison.root = wrapper.querySelector('.msv-pane');
        comparison.pane = comparison.root;
        comparison.root.classList.add('msv-comparison');
        comparison.root.setAttribute('aria-label', 'Second source: ' + comparison.source.name);
        comparison.root.setAttribute('role', 'region');
        el(comparison, '[data-msv-pull-slide]').hidden = true;
        el(comparison, '[data-msv-close]').setAttribute('aria-label', 'Close comparison');
        el(comparison, '[data-msv-close]').title = 'Close comparison';
        view.root.querySelector('.msv-panel').append(comparison.root);
        bind(comparison);
        setThumbsOpen(view, false);
        setThumbsOpen(comparison, true);
        view.root.classList.add('msv-duo');
        updateChrome(view);
        void load(comparison);
        el(comparison, '[data-msv-close]').focus({ preventScroll: true });
    }

    function closeComparison(view) {
        if (!view.comparison) return;
        teardown(view.comparison);
        view.comparison = null;
        view.keyboardView = view;
        view.root.classList.remove('msv-duo');
        updateChrome(view);
        setThumbsOpen(view, view.thumbsBeforeComparison);
        el(view, '[data-msv-pull-slide]').focus({ preventScroll: true });
    }

    // ---- Region selection ----

    function ensureSelectionApi(view) {
        if (global.MemoraSourceSelection?.commit) return Promise.resolve(global.MemoraSourceSelection);
        if (!view.selectionApiPromise) {
            const notice = global.setTimeout(() => { if (isCurrent(view)) setNotice(view, 'Loading Ask…'); }, ASK_LOADING_NOTICE_DELAY_MS);
            view.selectionApiPromise = Promise.resolve(global.MemoraRuntime?.ensureFeature?.('ai'))
                .then(() => {
                    if (!global.MemoraSourceSelection?.commit) throw new Error('Ask is not available.');
                    return global.MemoraSourceSelection;
                })
                .finally(() => {
                    global.clearTimeout(notice);
                    if (isCurrent(view)) setNotice(view, '');
                });
            // A failed load can be retried by the next box.
            view.selectionApiPromise.catch(() => { view.selectionApiPromise = null; });
        }
        return view.selectionApiPromise;
    }

    function layerPoint(view, event) {
        const layer = el(view, '.msv-layer');
        const rect = layer?.getBoundingClientRect?.();
        if (!rect?.width || !rect?.height) return null;
        const clamp = (value) => Math.max(0, Math.min(1, value));
        return {
            x: clamp((event.clientX - rect.left) / rect.width),
            y: clamp((event.clientY - rect.top) / rect.height)
        };
    }

    function draftGeometry(start, end) {
        return {
            x: Math.min(start.x, end.x),
            y: Math.min(start.y, end.y),
            width: Math.abs(end.x - start.x),
            height: Math.abs(end.y - start.y)
        };
    }

    // Same floor as the Ask bundle's drag rules, so a click never loads Ask.
    function isMeaningfulRegion(region) {
        return region.width >= 0.006 && region.height >= 0.006 && region.width * region.height >= 0.00008;
    }

    function paintDraft(draft, region) {
        draft.style.left = (region.x * 100).toFixed(2) + '%';
        draft.style.top = (region.y * 100).toFixed(2) + '%';
        draft.style.width = Math.max(0.3, region.width * 100).toFixed(2) + '%';
        draft.style.height = Math.max(0.3, region.height * 100).toFixed(2) + '%';
    }

    function isSelectionIgnoredTarget(target) {
        return !!target?.closest?.([
            '.pdf-evidence-selection-hitbox',
            '.pdf-tutor-focus-ask-card',
            '.pdf-tutor-focus-action-row',
            'button',
            'a',
            'input',
            'textarea',
            'select',
            'label'
        ].join(', '));
    }

    function cancelDrawing(view) {
        const drawing = view.drawing;
        if (!drawing) return;
        view.drawing = null;
        global.clearTimeout(drawing.pressTimer);
        try { drawing.layer.releasePointerCapture?.(drawing.pointerId); } catch (_error) { }
        drawing.draft?.remove();
        view.root.classList.remove('msv-is-drawing');
    }

    function beginDrawing(view, drawing) {
        drawing.active = true;
        const draft = d.createElement('div');
        draft.className = 'msv-draft';
        drawing.layer.append(draft);
        drawing.draft = draft;
        paintDraft(draft, draftGeometry(drawing.start, drawing.start));
        try { drawing.layer.setPointerCapture?.(drawing.pointerId); } catch (_error) { }
        view.root.classList.add('msv-is-drawing');
    }

    function handlePointerDown(view, event) {
        if (!view.capabilities.regionSelection || view.drawing || event.isPrimary === false) return;
        if (event.pointerType !== 'touch' && event.button !== 0) return;
        const target = event.target instanceof global.Element ? event.target : null;
        if (isSelectionIgnoredTarget(target)) return;
        const layer = el(view, '.msv-layer');
        const start = layerPoint(view, event);
        if (!layer || !start || el(view, '.msv-page').hidden) return;
        /* While the next page loads, or after it failed to render, the canvas on screen is
           still the previous page; a box drawn on it must not be filed under the new one. */
        if (view.displayedPage !== view.page) return;
        const drawing = {
            pointerId: event.pointerId,
            pointerType: event.pointerType,
            layer,
            start,
            end: start,
            clientX: event.clientX,
            clientY: event.clientY,
            active: false,
            pageToken: view.page + ':' + view.renderToken
        };
        view.drawing = drawing;
        if (event.pointerType === 'touch') {
            /* One finger scrolls and pinches as usual. Holding still starts a box, which
               is the iPad convention for "select here" and never steals a scroll. */
            drawing.pressTimer = global.setTimeout(() => {
                if (view.drawing === drawing) beginDrawing(view, drawing);
            }, LONG_PRESS_MS);
            return;
        }
        event.preventDefault();
        beginDrawing(view, drawing);
    }

    function handlePointerMove(view, event) {
        const drawing = view.drawing;
        if (!drawing || event.pointerId !== drawing.pointerId) return;
        if (!drawing.active) {
            if (Math.hypot(event.clientX - drawing.clientX, event.clientY - drawing.clientY) > TOUCH_SLOP_PX) cancelDrawing(view);
            return;
        }
        event.preventDefault();
        /* The open Ask card gives way only once this is really a new box. A plain click,
           such as the one that brings the window back from DevTools or a screenshot tool,
           must leave the card where it was. */
        if (!drawing.replacedAsk && Math.hypot(event.clientX - drawing.clientX, event.clientY - drawing.clientY) > TOUCH_SLOP_PX) {
            drawing.replacedAsk = true;
            global.MemoraSourceSelection?.closeAskCard?.(view.root);
        }
        const point = layerPoint(view, event);
        if (!point) return;
        drawing.end = point;
        paintDraft(drawing.draft, draftGeometry(drawing.start, point));
    }

    function handlePointerUp(view, event) {
        const drawing = view.drawing;
        if (!drawing || event.pointerId !== drawing.pointerId) return;
        if (!drawing.active) { cancelDrawing(view); return; }
        event.preventDefault();
        const end = layerPoint(view, event) || drawing.end;
        const region = draftGeometry(drawing.start, end);
        view.drawing = null;
        global.clearTimeout(drawing.pressTimer);
        try { drawing.layer.releasePointerCapture?.(drawing.pointerId); } catch (_error) { }
        view.root.classList.remove('msv-is-drawing');
        if (!isMeaningfulRegion(region)) { drawing.draft?.remove(); return; }
        drawing.draft.classList.add('is-pending');
        void commitSelection(view, drawing, end);
    }

    /* On a standalone image the box gets the same highlight controls as a file (Ask, Save to
       Notes, remove), from MemoraSourceSelection.commitImage. What Ask and Notes receive is the
       image's own pixels for that area - never a nearby slide - read only when one is used. */
    async function commitImageSelection(view, drawing, end) {
        let api = null;
        try {
            api = await ensureSelectionApi(view);
        } catch (error) {
            drawing.draft?.remove();
            if (isCurrent(view)) setNotice(view, 'Ask could not load. Check your connection, then draw the box again.');
            console.warn('Source viewer could not load Ask:', error);
            return;
        }
        drawing.draft?.remove();
        if (!isCurrent(view) || typeof api.commitImage !== 'function') return;
        const region = draftGeometry(drawing.start, end);
        const hitbox = api.commitImage({
            layer: drawing.layer,
            start: drawing.start,
            end,
            source: {
                title: view.source.title || view.source.name,
                url: view.source.url,
                sourceUrl: view.source.attribution?.href || '',
                caption: view.source.alt
            },
            readCrop: () => readImageArea(view, region)
        });
        if (!hitbox) setNotice(view, 'That area is too small to ask about. Drag a larger box.', { autoHide: true });
    }

    /* A picture from a site that refuses CORS is read through the relay's image route instead,
       fetched once per view and only when a box is actually used. Only if that fails too does
       the notice say why. */
    function loadRelayImageCopy(view) {
        if (view.relayImage) return view.relayImage;
        const base = String(global.PUBLIC_APP_CONFIG?.aiRelayUrl || '').replace(/\/$/, '');
        const url = view.source.url;
        if (!base || !/^https:/i.test(url || '')) return Promise.reject(new Error('No relay.'));
        view.relayImage = global.fetch(`${base}/v1/images/fetch?url=${encodeURIComponent(url)}`, { credentials: 'omit', signal: view.abortController.signal })
            .then((response) => (response.ok ? response.blob() : Promise.reject(new Error(`Relay HTTP ${response.status}`))))
            .then((blob) => {
                const objectUrl = URL.createObjectURL(blob);
                view.release.push(() => URL.revokeObjectURL(objectUrl));
                return loadImageElement(objectUrl, false, view.abortController.signal);
            });
        view.relayImage.catch(() => { view.relayImage = null; });
        return view.relayImage;
    }

    function readImageArea(view, region) {
        const refused = 'This picture\'s website does not let Memoraful read its pixels, so this area cannot be sent to Ask or Notes. Open the original from the link above.';
        if (view.imagePixelsReadable === false) {
            return loadRelayImageCopy(view).then((image) => cropImageRegion(view, region, image)).catch((error) => {
                if (error?.name === 'AbortError') throw error;
                if (isCurrent(view)) setNotice(view, refused);
                const refusal = new Error(refused);
                refusal.name = 'SecurityError';
                throw refusal;
            });
        }
        return cropImageRegion(view, region).catch((error) => {
            if (isCurrent(view)) setNotice(view, error?.name === 'SecurityError' ? refused : 'Could not read that area. Try drawing the box again.');
            throw error;
        });
    }

    const IMAGE_CROP_MAX_EDGE = 1600;

    function cropImageRegion(view, region, image = view.imageElement) {
        return new Promise((resolve, reject) => {
            if (!image) { reject(new Error('The image is not loaded.')); return; }
            const width = Math.max(1, Number(image.naturalWidth) || 1);
            const height = Math.max(1, Number(image.naturalHeight) || 1);
            const sx = Math.floor(region.x * width);
            const sy = Math.floor(region.y * height);
            const sw = Math.max(1, Math.min(width - sx, Math.ceil(region.width * width)));
            const sh = Math.max(1, Math.min(height - sy, Math.ceil(region.height * height)));
            const scale = Math.min(1, IMAGE_CROP_MAX_EDGE / Math.max(sw, sh));
            const canvas = d.createElement('canvas');
            canvas.width = Math.max(1, Math.round(sw * scale));
            canvas.height = Math.max(1, Math.round(sh * scale));
            const context = canvas.getContext('2d');
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            // A picture from a site that refused CORS taints the canvas and toBlob throws.
            try {
                canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not read that area.'))), 'image/jpeg', 0.9);
            } catch (error) {
                reject(error);
            }
        });
    }

    async function commitSelection(view, drawing, end) {
        if (view.source.kind === 'image') { await commitImageSelection(view, drawing, end); return; }
        let api = null;
        try {
            api = await ensureSelectionApi(view);
        } catch (error) {
            drawing.draft?.remove();
            if (isCurrent(view)) setNotice(view, 'Ask could not load. Check your connection, then draw the box again.');
            console.warn('Source viewer could not load Ask:', error);
            return;
        }
        drawing.draft?.remove();
        // The page or its render may have moved on while Ask loaded; that box belongs to nothing now.
        if (!isCurrent(view) || drawing.pageToken !== view.page + ':' + view.renderToken) return;
        const hitbox = api.commit({ host: el(view, '.msv-page'), layer: drawing.layer, start: drawing.start, end });
        if (!hitbox) {
            setNotice(view, 'That area is too small to ask about. Drag a larger box.', { autoHide: true });
            return;
        }
        // The box opened its Ask, so this slide's highlights are on screen until that Ask closes.
        view.highlightsShownPages.add(view.page);
        syncHighlightsButton(view);
    }

    // ---- Saved highlights ----

    /* Highlights belong to their Ask: a finished box opens Ask, and closing Ask hides that
       slide's highlights again. The header toggle is per slide: it appears only on a slide that
       has a saved highlight, reads "Show highlights" / "Hide highlights", and is gone on a slide
       without one. A source with no saved highlight never loads Ask to learn that: the stored
       selections are read raw first, and Ask loads only when there is something to draw. */
    const SELECTIONS_STORE_KEY = 'pdf_tutor_custom_selections_v1';

    function highlightsShownOnPage(view) {
        return view.highlightsShownPages.has(view.page);
    }

    async function readHighlightPages(view) {
        const pageEl = el(view, '.msv-page');
        if (global.MemoraSourceSelection?.storedPages) return global.MemoraSourceSelection.storedPages(pageEl);
        let payload = null;
        try {
            payload = typeof IDB !== 'undefined' ? await IDB.get('settings', SELECTIONS_STORE_KEY) : null;
        } catch (_error) {
            payload = null;
        }
        const hasAny = Object.values(payload?.selections || {}).some((selection) => (
            String(selection?.pdfName || '').trim() === view.source.name
            && selection?.region?.source === 'evidence-pdf'
        ));
        if (!hasAny) return {};
        const api = await ensureSelectionApi(view);
        return api.storedPages ? api.storedPages(pageEl) : {};
    }

    // Recounts this source's saved highlights; `render` also draws the current page's.
    async function refreshHighlights(view, { render = false } = {}) {
        if (!isCurrent(view) || view.source.kind === 'image' || !view.adapter) return;
        const seq = (view.highlightSeq || 0) + 1;
        view.highlightSeq = seq;
        let pages = {};
        try {
            pages = (await readHighlightPages(view)) || {};
        } catch (_error) {
            pages = {};
        }
        if (!isCurrent(view) || seq !== view.highlightSeq) return;
        view.highlightPages = pages;
        syncHighlightsButton(view);
        if (render && pages[view.page] && highlightsShownOnPage(view)) void revealHighlights(view);
    }

    function syncHighlightsButton(view) {
        const button = el(view, '[data-msv-highlights]');
        if (!button) return;
        // Per slide: only this slide's saved highlights reveal the toggle.
        button.hidden = view.source.kind === 'image' || !(Number((view.highlightPages || {})[view.page]) > 0);
        const shown = highlightsShownOnPage(view);
        button.dataset.state = shown ? 'shown' : 'hidden';
        button.setAttribute('aria-pressed', String(shown));
        const label = button.querySelector('.msv-highlights-label');
        if (label) label.textContent = shown ? 'Hide highlights' : 'Show highlights';
        button.title = shown ? 'Hide this slide\'s highlights' : 'Show this slide\'s highlights';
        button.setAttribute('aria-label', button.title);
    }

    async function revealHighlights(view) {
        const pageToken = view.page + ':' + view.renderToken;
        let api = null;
        try {
            api = await ensureSelectionApi(view);
        } catch (_error) {
            if (isCurrent(view)) setNotice(view, 'Highlights could not load. Try again in a moment.');
            return;
        }
        if (!isCurrent(view) || !highlightsShownOnPage(view) || pageToken !== view.page + ':' + view.renderToken) return;
        await api.renderStored(el(view, '.msv-page'));
    }

    function showHighlights(view) {
        view.highlightsShownPages.add(view.page);
        syncHighlightsButton(view);
        void revealHighlights(view);
    }

    // `fromAskClose`: Ask already closed itself; anything else closes the open card first.
    function hideHighlights(view, { fromAskClose = false } = {}) {
        view.highlightsShownPages.delete(view.page);
        if (!fromAskClose) global.MemoraSourceSelection?.closeAskCard?.(view.root);
        global.MemoraSourceSelection?.clearStored?.(el(view, '.msv-page'));
        syncHighlightsButton(view);
    }

    function handleHighlightsClick(view) {
        if (highlightsShownOnPage(view)) hideHighlights(view);
        else showHighlights(view);
    }

    function closeHighlightsMenu(view, { restoreFocus = false } = {}) {
        const menu = view.highlightsMenu;
        if (!menu) return false;
        view.highlightsMenu = null;
        menu.remove();
        const button = el(view, '[data-msv-highlights]');
        button?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) button?.focus({ preventScroll: true });
        return true;
    }

    // ---- Spotlight ----

    /* A brief Guided-style focus for the voice: the slide dims except the area being talked
       about, then the dim fades by itself. It takes no input, and a page turn or close clears it. */
    function clearSpotlight(view) {
        const spot = view?.spotlight;
        if (!spot) return;
        view.spotlight = null;
        global.clearTimeout(spot.timer);
        spot.node.remove();
    }

    function spotlight(view, { page, regions, holdMs = SPOTLIGHT_HOLD_MS } = {}) {
        if (!view || !isCurrent(view) || view.source.kind === 'image' || view.cropRect) return false;
        if (page !== undefined && Number(page) !== (view.displayedPage || view.page)) return false;
        const boxes = (Array.isArray(regions) ? regions : [regions]).map(normalizeCropRect).filter(Boolean).slice(0, 4);
        const layer = el(view, '.msv-layer');
        if (!boxes.length || !layer) return false;
        clearSpotlight(view);
        const ns = 'http://www.w3.org/2000/svg';
        const maskId = 'msv-spotlight-mask-' + view.id;
        const svg = d.createElementNS(ns, 'svg');
        svg.setAttribute('class', 'msv-spotlight');
        svg.setAttribute('viewBox', '0 0 1000 1000');
        svg.setAttribute('preserveAspectRatio', 'none');
        svg.setAttribute('aria-hidden', 'true');
        // Guided's soft-shape mask: each hole is padded and blurred, so light falls off around the
        // area instead of stopping at an edge. No outline is drawn.
        const hole = (box) => {
            const padX = Math.min(0.045, Math.max(0.018, box.width * 0.16));
            const padY = Math.min(0.045, Math.max(0.018, box.height * 0.16));
            const x = (box.x - padX) * 1000;
            const y = (box.y - padY) * 1000;
            const width = (box.width + padX * 2) * 1000;
            const height = (box.height + padY * 2) * 1000;
            return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${width.toFixed(1)}" height="${height.toFixed(1)}" `
                + `rx="${Math.min(width / 2, padX * 1000).toFixed(1)}" ry="${Math.min(height / 2, padY * 1000).toFixed(1)}" fill="black" filter="url(#${maskId}-feather)"/>`;
        };
        svg.innerHTML = `<defs><filter id="${maskId}-feather" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="24"/></filter>`
            + `<mask id="${maskId}" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000"><rect width="1000" height="1000" fill="white"/>`
            + boxes.map(hole).join('') + '</mask></defs>'
            + `<rect class="msv-spotlight-dim" width="1000" height="1000" mask="url(#${maskId})"/>`;
        layer.append(svg);
        const spot = { node: svg, timer: 0 };
        view.spotlight = spot;
        global.requestAnimationFrame(() => { if (view.spotlight === spot) svg.classList.add('is-on'); });
        spot.timer = global.setTimeout(() => {
            if (view.spotlight !== spot) return;
            svg.classList.remove('is-on');
            spot.timer = global.setTimeout(() => { if (view.spotlight === spot) clearSpotlight(view); }, SPOTLIGHT_FADE_MS);
        }, Math.max(300, Number(holdMs) || SPOTLIGHT_HOLD_MS));
        return true;
    }

    // ---- Keyboard ----

    function isEditableTarget(target) {
        return !!target?.closest?.('input, textarea, select, [contenteditable="true"], .pdf-tutor-focus-ask-card');
    }

    function handleKeydown(view, event) {
        // Escape and Tab belong to the modal coordinator, which saw them first.
        if (event.key === 'Escape' || event.key === 'Tab') return;
        // The voice agent toggle (Shift+B) stays global while the viewer is open.
        if (global.matchesShortcutActionEvent?.(event, 'toggleAgent')) return;
        // Every other key stops here, so quiz and Tutor hotkeys behind the viewer stay quiet.
        event.stopPropagation();
        if (isEditableTarget(event.target) || event.altKey || event.ctrlKey || event.metaKey) return;
        const target = event.target instanceof global.Element ? event.target : null;
        /* Left and Right turn the page from anywhere in the viewer, including its buttons
           (excluding buttons left the arrows dead after a click). Only an open menu keeps its keys. */
        const inMenu = !!target?.closest?.('.msv-highlights-menu, .msv-source-picker');
        if (inMenu) return;
        if ((event.key === 'ArrowRight' || event.key === 'PageDown') && !inMenu) {
            event.preventDefault();
            void goToPage(view, view.page + 1);
        } else if ((event.key === 'ArrowLeft' || event.key === 'PageUp') && !inMenu) {
            event.preventDefault();
            void goToPage(view, view.page - 1);
        } else if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            stepZoom(view, 1);
        } else if (event.key === '-') {
            event.preventDefault();
            stepZoom(view, -1);
        } else if (event.key === '0') {
            event.preventDefault();
            setZoom(view, 1);
        }
    }

    // ---- Ask card portal ----

    /* A submitted Ask card moves to a body-level portal to keep its viewport position. Inside
       this viewer that would put it under the viewer and outside the coordinator's focus trap,
       so the portal lives inside the viewer while it is open. Lookups are by id, so its owners
       find it either way. */
    function adoptAskPortal(view) {
        let portal = d.getElementById(ASK_PORTAL_ID);
        if (!portal) {
            portal = d.createElement('div');
            portal.id = ASK_PORTAL_ID;
            portal.className = 'pdf-tutor-focus-ask-portal';
        }
        view.portalHome = portal.parentNode && portal.parentNode !== view.root ? portal.parentNode : d.body;
        view.root.append(portal);
    }

    function returnAskPortal(view) {
        const portal = d.getElementById(ASK_PORTAL_ID);
        if (!portal || !view.root.contains(portal)) return;
        (view.portalHome?.isConnected ? view.portalHome : d.body).append(portal);
    }

    // ---- Surfaces opened from inside the viewer ----

    /* Save to Notes opens the Notes editor, which does not register with the modal
       coordinator. With the viewer as the top layer, the coordinator's focus trap would pull
       focus straight back out of the editor. While the viewer is open, Notes is registered as
       its child layer, so focus lives in Notes until it closes and then returns here. */
    const NESTED_SURFACES = [{
        id: 'voice-agent-modal',
        // The rail never restores an origin when Voice closes, so Voice leaves navigation free to close the viewer.
        holdsViewer: false,
        root: () => d.getElementById('voice-agent-modal'),
        element: () => d.querySelector('#voice-agent-modal .voice-agent-modal-content') || d.getElementById('voice-agent-modal'),
        attributes: ['class', 'data-over-source-viewer'],
        isOpen: (root) => !!root?.classList.contains('active') && root.dataset.overSourceViewer === 'true',
        close: () => global.closeVoiceAgentModal?.('button')
    }, {
        id: 'notes-modal',
        root: () => d.getElementById('notes-modal'),
        element: () => d.getElementById('notes-modal-content') || d.getElementById('notes-modal'),
        isOpen: (root) => !!root?.classList.contains('active') && !root.classList.contains('is-closing'),
        close: (root) => global.closeNotesModal?.({ target: root })
    }, {
        /* The study panel (web images, a study visual, Lecture Finder) shown while the viewer is
           open comes up over it, marked data-over-source-viewer by showPdfTutorSlideFinderPanel.
           As the child layer it keeps focus and takes Escape until it closes. */
        id: 'pdf-tutor-slide-finder-panel',
        root: () => d.getElementById('pdf-tutor-slide-finder-panel'),
        element: () => d.getElementById('pdf-tutor-slide-finder-panel'),
        attributes: ['hidden', 'data-over-source-viewer'],
        isOpen: (root) => !!root && !root.hidden && root.dataset.overSourceViewer === 'true',
        close: () => global.closePdfTutorSlideFinderPanel?.()
    }];
    const OVER_VIEWER_ATTRIBUTE = 'data-over-source-viewer';

    /* Settings, opened from an Ask card's gear, registers its own coordinator layer, but the
       rail admits it as a transient just as it does Notes, and restoring that origin when it
       closes would take this viewer and its Ask card with it. Only the hold and the focus
       return apply to it. */
    const HELD_SURFACES = [{
        id: 'settings-modal',
        root: () => d.getElementById('settings-modal'),
        isOpen: (root) => !!root?.classList.contains('active')
    }];

    function syncNestedSurfaces(view) {
        const coordinator = global.MemoraModalCoordinator;
        if (!isCurrent(view)) return;
        HELD_SURFACES.forEach((surface) => {
            const open = surface.isOpen(surface.root());
            const held = !!view.nestedOpeners[surface.id];
            if (open && !held) {
                view.holdThroughRailRestore = true;
                // Settings has usually taken focus by now; the Ask card's composer is where to return.
                view.nestedOpeners[surface.id] = view.root.contains(d.activeElement)
                    ? d.activeElement
                    : (el(view, '.pdf-tutor-focus-ask-input') || el(view, '[data-msv-close]'));
            } else if (!open && held) {
                const opener = view.nestedOpeners[surface.id];
                delete view.nestedOpeners[surface.id];
                [0, 320].forEach((delay) => global.setTimeout(() => refocusInside(view, opener), delay));
            }
        });
        if (!coordinator?.open) return;
        NESTED_SURFACES.forEach((surface) => {
            const root = surface.root();
            const open = surface.isOpen(root);
            // Tracked here: the coordinator stops reporting the layer open the moment Notes is inactive.
            const registered = !!view.nestedOpeners[surface.id];
            if (open && !registered) {
                /* The rail admits Notes as a transient over the Dashboard or picker and
                   restores that origin once Notes closes, often before this observer hears
                   about the close. That restore closes every coordinator layer as a companion
                   surface, which would take this viewer with it although the viewer is still
                   the foreground over that origin. Hold the viewer open from here until the
                   next interaction with it after Notes has gone. */
                if (surface.holdsViewer !== false) view.holdThroughRailRestore = true;
                view.nestedOpeners[surface.id] = view.root.contains(d.activeElement) ? d.activeElement : el(view, '[data-msv-close]');
                coordinator.open({
                    id: surface.id,
                    element: surface.element(),
                    opener: view.nestedOpeners[surface.id],
                    parentLayerId: LAYER_ID,
                    restoreFocus: true,
                    trapFocus: true,
                    isOpen: () => surface.isOpen(surface.root()),
                    close: () => { surface.close(surface.root()); return true; }
                });
            } else if (!open && registered) {
                coordinator.markClosed(surface.id, { restore: true });
                const opener = view.nestedOpeners[surface.id];
                delete view.nestedOpeners[surface.id];
                // The rail's restore of Notes' origin moves focus after this; take it back once it settles.
                [0, 320].forEach((delay) => global.setTimeout(() => refocusInside(view, opener), delay));
            }
        });
    }

    function refocusInside(view, preferred) {
        if (!isCurrent(view) || view.root.contains(d.activeElement)) return;
        const coordinator = global.MemoraModalCoordinator;
        const stack = coordinator?.getStack?.() || [];
        if (stack.length && stack[stack.length - 1] !== LAYER_ID) return;
        const target = preferred?.isConnected && view.root.contains(preferred) && preferred.getClientRects().length
            ? preferred
            : el(view, '[data-msv-close]');
        target?.focus?.({ preventScroll: true });
    }

    function watchNestedSurfaces(view) {
        if (!global.MutationObserver) return;
        view.nestedObserver = new global.MutationObserver(() => syncNestedSurfaces(view));
        [...NESTED_SURFACES, ...HELD_SURFACES].forEach((surface) => {
            const root = surface.root();
            if (root) view.nestedObserver.observe(root, { attributes: true, attributeFilter: surface.attributes || ['class'] });
        });
    }

    function releaseNestedSurfaces() {
        const coordinator = global.MemoraModalCoordinator;
        NESTED_SURFACES.forEach((surface) => {
            if (coordinator?.isOpen?.(surface.id)) coordinator.markClosed(surface.id);
        });
    }

    // ---- Lifecycle ----

    function teardown(view) {
        if (view.closed) return;
        closeSourcePicker(view);
        if (view.comparison) { teardown(view.comparison); view.comparison = null; }
        closeHighlightsMenu(view);
        view.closed = true;
        if (active === view) active = null;
        cancelDrawing(view);
        clearSpotlight(view);
        global.clearTimeout(view.sharpenTimer);
        global.clearTimeout(view.noticeHideTimer);
        global.clearTimeout(view.noticeFadeTimer);
        try { global.MemoraSourceSelection?.closeAskCard?.(view.root); } catch (_error) { }
        view.abortController.abort();
        view.thumbObserver?.disconnect();
        view.resizeObserver?.disconnect();
        view.nestedObserver?.disconnect();
        if (!view.parent) releaseNestedSurfaces();
        view.release.splice(0).forEach((release) => { try { release(); } catch (_error) { } });
        if (!view.parent) returnAskPortal(view);
        view.root.querySelectorAll('canvas').forEach((canvas) => { canvas.width = 0; canvas.height = 0; });
        view.root.remove();
        view.imageElement = null;
        view.adapter = null;
        if (!active) {
            d.body.classList.remove(BODY_OPEN_CLASS);
            delete d.body.dataset.msvVoiceSeat;
        }
        publishViewerContext();
        view.resolveReady(false);
        notifyOwner(view, 'onClose');
        // Closed coordinator entries keep their callbacks until the next open. Drop owner
        // callbacks and rendered nodes here so the entry cannot retain their study context.
        view.onClose = null;
        view.onPageChange = null;
        view.root.replaceChildren();
    }

    function closeView(view, reason = 'programmatic') {
        if (!view || view.closed) return false;
        if (view.parent) { closeComparison(view.parent); return true; }
        const coordinator = global.MemoraModalCoordinator;
        if (coordinator?.isOpen?.(LAYER_ID) && coordinator.close(LAYER_ID, reason)) return true;
        const opener = view.origin.opener;
        teardown(view);
        if (opener?.isConnected) opener.focus({ preventScroll: true });
        return true;
    }

    function bind(view) {
        const root = view.root;
        bindZoomGestures(view);
        el(view, '[data-msv-close]').addEventListener('click', () => closeView(view, 'button'));
        root.querySelector('[data-msv-dismiss]')?.addEventListener('click', () => closeView(view, 'backdrop'));
        el(view, '[data-msv-pull-slide]').addEventListener('click', () => {
            if (!closeSourcePicker(view)) openSourcePicker(view);
        });
        el(view, '[data-msv-prev]').addEventListener('click', () => { void goToPage(view, view.page - 1); });
        el(view, '[data-msv-next]').addEventListener('click', () => { void goToPage(view, view.page + 1); });
        el(view, '[data-msv-zoom-in]').addEventListener('click', () => stepZoom(view, 1));
        el(view, '[data-msv-zoom-out]').addEventListener('click', () => stepZoom(view, -1));
        el(view, '[data-msv-fit]').addEventListener('click', () => setZoom(view, 1));
        el(view, '[data-msv-highlights]').addEventListener('click', () => handleHighlightsClick(view));
        el(view, '[data-msv-thumbs]').addEventListener('click', () => setThumbsOpen(view, el(view, '.msv-thumbs').hidden));
        el(view, '.msv-thumbs').addEventListener('click', (event) => {
            const button = event.target instanceof global.Element ? event.target.closest('button[data-msv-page]') : null;
            if (button) void goToPage(view, Number(button.dataset.msvPage));
        });
        root.addEventListener('keydown', (event) => handleKeydown(view, event));
        root.addEventListener('pointerdown', () => {
            if (active) active.keyboardView = view;
        }, true);
        root.addEventListener('focusin', (event) => {
            if (active) active.keyboardView = event.target.closest?.('.msv-comparison') ? active.comparison : view;
        });
        // Focus can drop to <body> (a click on the backdrop edge, a closed popup); the viewer
        // is still the foreground, so its page keys still apply there.
        global.addEventListener('keydown', (event) => {
            if (view.parent || !isCurrent(view) || event.defaultPrevented) return;
            const target = event.target;
            if (target !== d.body && target !== d.documentElement) return;
            if (!['ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown'].includes(event.key)) return;
            handleKeydown(view.keyboardView || view, event);
        }, { capture: true, signal: view.abortController.signal });
        const endRailRestoreHold = () => { view.holdThroughRailRestore = false; };
        root.addEventListener('pointerdown', endRailRestoreHold, true);
        root.addEventListener('keydown', endRailRestoreHold, true);
        // A press anywhere but the menu or its button closes the Highlights menu.
        root.addEventListener('pointerdown', (event) => {
            if (view.sourcePicker && !event.target.closest?.('.msv-source-picker, [data-msv-pull-slide]')) closeSourcePicker(view);
            if (!view.highlightsMenu || !(event.target instanceof global.Element)) return;
            if (event.target.closest('.msv-highlights-menu, [data-msv-highlights]')) return;
            closeHighlightsMenu(view);
        }, true);
        // A highlight saved or removed anywhere may add or retire this source's button.
        global.addEventListener('memora:custom-selections-changed', () => { void refreshHighlights(view); }, { signal: view.abortController.signal });
        // Closing a highlight's Ask hides that slide's highlights; the toggle then reads "Show highlights".
        global.addEventListener('memora:focus-ask-closed', (event) => {
            const hotspot = event.detail?.hotspot;
            if (!isCurrent(view) || !hotspot || !el(view, '.msv-page')?.contains(hotspot)) return;
            if (highlightsShownOnPage(view)) hideHighlights(view, { fromAskClose: true });
        }, { signal: view.abortController.signal });
        const layer = el(view, '.msv-layer');
        layer.addEventListener('pointerdown', (event) => handlePointerDown(view, event));
        layer.addEventListener('pointermove', (event) => handlePointerMove(view, event));
        layer.addEventListener('pointerup', (event) => handlePointerUp(view, event));
        layer.addEventListener('pointercancel', () => cancelDrawing(view));
        /* Leaving the window mid-press (DevTools, a screenshot tool) can swallow the pointerup.
           A box still waiting for it would block every later box, so let it go. */
        layer.addEventListener('lostpointercapture', (event) => {
            if (view.drawing?.pointerId === event.pointerId) cancelDrawing(view);
        });
        global.addEventListener('blur', () => cancelDrawing(view), { signal: view.abortController.signal });
        // Once a touch box has started, the finger must draw rather than scroll the stage.
        layer.addEventListener('touchmove', (event) => { if (view.drawing?.active) event.preventDefault(); }, { passive: false });
        layer.addEventListener('contextmenu', (event) => { if (view.drawing) event.preventDefault(); });
        if (global.ResizeObserver) {
            view.resizeObserver = new global.ResizeObserver(() => {
                if (!isCurrent(view)) return;
                applyLayout(view);
                scheduleSharpen(view);
                seatVoiceOrb(view);
            });
            view.resizeObserver.observe(el(view, '.msv-stage'));
            // The slot opens when Voice connects with the viewer already up.
            const slot = view.root?.querySelector('.msv-voice-slot');
            if (slot && !view.parent) view.resizeObserver.observe(slot);
            // The title takes the toolbar's spare width, so it resizes whenever a sibling moves the slot.
            const identity = view.root?.querySelector('.msv-identity');
            if (slot && identity && !view.parent) view.resizeObserver.observe(identity);
        }
    }

    function registerLayer(view) {
        const coordinator = global.MemoraModalCoordinator;
        if (!coordinator?.open) {
            global.requestAnimationFrame?.(() => { if (isCurrent(view)) view.root.focus({ preventScroll: true }); });
            return;
        }
        const stack = coordinator.getStack?.() || [];
        coordinator.open({
            id: LAYER_ID,
            element: view.root,
            // The dialog itself takes focus, so no toolbar button opens highlighted.
            focus: () => { view.root.focus({ preventScroll: true }); },
            opener: view.origin.opener,
            parentLayerId: stack.length ? stack[stack.length - 1] : null,
            restoreFocus: true,
            trapFocus: true,
            isOpen: () => isCurrent(view),
            close: (reason) => {
                if (reason === 'rail' && view.holdThroughRailRestore) return { closed: false };
                // Escape closes the innermost thing first: a menu, an open Ask card, then the viewer.
                if (reason === 'escape') {
                    if (closeSourcePicker(view, { restoreFocus: true })) return { closed: false };
                    if (closeHighlightsMenu(view.comparison || view, { restoreFocus: true })) return { closed: false };
                    if (view.comparison && closeHighlightsMenu(view, { restoreFocus: true })) return { closed: false };
                    const drawingView = view.comparison?.drawing ? view.comparison : view;
                    if (drawingView.drawing) { cancelDrawing(drawingView); return { closed: false }; }
                    if (global.MemoraSourceSelection?.closeAskCard?.(view.root)) return { closed: false };
                }
                teardown(view);
                return true;
            }
        });
    }

    async function load(view) {
        try {
            const adapter = view.adapter || (view.source.kind === 'image'
                ? await createImageAdapter(view)
                : (view.source.kind === 'pptx' ? await createPptxAdapter(view) : await createPdfAdapter(view)));
            if (!isCurrent(view) || !adapter) return;
            view.adapter = adapter;
            view.page = Math.min(view.page, adapter.count);
            if (view.page !== view.source.page) view.cropRect = null;
            syncHostIdentity(view);
            buildThumbnails(view);
            applyLayout(view);
            updateChrome(view);
            const rendered = await renderPage(view);
            view.resolveReady(rendered && isCurrent(view));
            if (rendered) void refreshHighlights(view, { render: true });
        } catch (error) {
            if (!isCurrent(view)) return;
            setStatus(view, error?.message || 'A preview is not available for this file.');
            view.resolveReady(false);
        }
    }

    function createView(normalized) {
        let resolveReady = () => {};
        const ready = new Promise((resolve) => { resolveReady = resolve; });
        const view = {
            id: ++sequence,
            source: normalized.source,
            origin: normalized.origin,
            capabilities: normalized.capabilities,
            onPageChange: normalized.onPageChange,
            onClose: normalized.onClose,
            page: normalized.source.page,
            cropRect: normalized.source.cropRect,
            zoom: 1,
            aspect: 0,
            adapter: null,
            renderToken: 0,
            renderedWidth: 0,
            displayedPage: 0,
            rendering: false,
            highlightPages: null,
            highlightsShownPages: new Set(),
            highlightsMenu: null,
            highlightSeq: 0,
            drawing: null,
            nestedOpeners: {},
            holdThroughRailRestore: false,
            closed: false,
            release: [],
            abortController: new AbortController(),
            resolveReady: (value) => { resolveReady(!!value); resolveReady = () => {}; }
        };
        view.ready = ready;
        return view;
    }

    function open(request = {}) {
        const normalized = normalizeRequest(request);
        if (!normalized.source.name) return null;
        const replacing = !!active;
        if (active) {
            const previous = active;
            // Another page of the file already on screen turns the page in place; rebuilding would
            // replay the launch and drop the student's zoom and page strip.
            if (!previous.closed && previous.adapter && !previous.comparison && !previous.sourcePicker
                && normalized.source.kind !== 'image' && previous.source.kind === normalized.source.kind
                && previous.source.name === normalized.source.name && !normalized.source.cropRect
                && previous.source.pageMemoryKey === normalized.source.pageMemoryKey && !previous.source.cropRect
                && !previous.onPageChange && !previous.onClose
                && !normalized.onPageChange && !normalized.onClose) {
                const page = normalized.source.page;
                const ready = goToPage(previous, page).then(() => isCurrent(previous) && previous.displayedPage === page, () => false);
                return Object.freeze({
                    id: previous.id,
                    ready,
                    close: () => closeView(previous, 'programmatic'),
                    goToPage: (pageNumber) => goToPage(previous, pageNumber)
                });
            }
            if (global.MemoraModalCoordinator?.isOpen?.(LAYER_ID)) global.MemoraModalCoordinator.markClosed(LAYER_ID);
            teardown(previous);
        }
        // A source opened now is the newest surface, so a panel raised over the last one drops back under it.
        d.querySelectorAll(`[${OVER_VIEWER_ATTRIBUTE}]`).forEach((node) => node.removeAttribute(OVER_VIEWER_ATTRIBUTE));
        const view = createView(normalized);
        const ready = view.ready;
        // Capture the preview before collapsing navigation shifts its position.
        const originRect = view.origin.opener?.getBoundingClientRect();
        view.root = buildRoot(view);
        // A different file replacing an open one swaps in place without the launch animation.
        if (replacing) view.root.classList.add('msv-replaced');
        view.pane = view.root.querySelector('.msv-pane');
        active = view;
        bind(view);
        adoptAskPortal(view);
        d.body.append(view.root);
        d.body.classList.add(BODY_OPEN_CLASS);
        seatVoiceOrb(view);
        publishViewerContext();
        // The viewer dims the page behind it, so the left rail keeps its width rather than collapsing.
        const panel = view.root.querySelector('.msv-panel');
        // The first seat is measured mid launch animation; this one is the resting toolbar.
        const seatAfterLaunch = (event) => {
            if (event.target !== panel) return;
            panel.removeEventListener('animationend', seatAfterLaunch);
            seatVoiceOrb(view);
        };
        panel.addEventListener('animationend', seatAfterLaunch);
        const panelRect = panel.getBoundingClientRect();
        if (originRect?.width && originRect?.height && panelRect.width && panelRect.height) {
            panel.style.setProperty('--msv-enter-x', (originRect.left + originRect.width / 2 - (panelRect.left + panelRect.width / 2)) + 'px');
            panel.style.setProperty('--msv-enter-y', (originRect.top + originRect.height / 2 - (panelRect.top + panelRect.height / 2)) + 'px');
            panel.style.setProperty('--msv-enter-scale-x', Math.max(0.04, originRect.width / panelRect.width));
            panel.style.setProperty('--msv-enter-scale-y', Math.max(0.04, originRect.height / panelRect.height));
        }
        el(view, '[data-msv-prev]').disabled = true;
        el(view, '[data-msv-next]').disabled = true;
        applyLayout(view);
        registerLayer(view);
        watchNestedSurfaces(view);
        void load(view);
        return Object.freeze({
            id: view.id,
            ready,
            close: () => closeView(view, 'programmatic'),
            goToPage: (pageNumber) => goToPage(view, pageNumber)
        });
    }

    // Voice/API admission uses exactly the picker-owned comparison pane and its renderer.
    async function openComparisonPair({ left, right, isCurrent: wanted = () => true } = {}) {
        if (!left?.file || !right?.file || !Number.isInteger(left.page) || !Number.isInteger(right.page)
            || left.page < 1 || right.page < 1 || !wanted()) return null;
        const receipt = open({ source: { name: left.file, page: left.page }, origin: { surface: 'voice-agent' } });
        const view = active;
        const valid = () => wanted() && isCurrent(view) && receipt?.id === view.id;
        const fail = () => { if (isCurrent(view)) closeView(view, 'comparison-failed'); return null; };
        if (!receipt || !await receipt.ready || !valid() || view.page !== left.page) return fail();
        openSourcePicker(view);
        const picker = view.sourcePicker;
        if (!picker) return fail();
        await choosePickerSource(view, picker, right.file);
        if (!valid() || !picker.adapter || right.page > picker.adapter.count) return fail();
        openComparison(view, picker, right.page);
        const comparison = view.comparison;
        if (!comparison) return fail();
        const ready = comparison.ready.then(rendered => {
            const ok = rendered && valid() && view.comparison === comparison && comparison.page === right.page;
            if (!ok && isCurrent(view)) closeView(view, 'comparison-failed');
            return !!ok;
        });
        return Object.freeze({ id: view.id, ready, close: () => closeComparison(view),
            readText: async () => ({ left: await view.adapter.readText?.(view.page) || '', right: await comparison.adapter.readText?.(comparison.page) || '' }) });
    }

    global.MemoraSourceViewer = Object.freeze({
        version: 1,
        open,
        openComparison: openComparisonPair,
        closeComparison: () => { if (active?.comparison) { closeComparison(active); return true; } return false; },
        close: (reason = 'programmatic') => closeView(active, reason),
        // Briefly dims the displayed page around normalized regions; false when it cannot show.
        spotlight: (options = {}) => spotlight(active, options),
        // Voice reads the displayed image through its owner, including its CORS and lifetime guards.
        readImageDataUrl: async ({ maxWidth = IMAGE_CROP_MAX_EDGE, quality } = {}) => {
            const view = active;
            if (!view || view.closed || view.source.kind !== 'image' || !view.adapter) return '';
            if (view.imagePixelsReadable === false) {
                try {
                    const image = await loadRelayImageCopy(view);
                    const blob = await cropImageRegion(view, { x: 0, y: 0, width: 1, height: 1 }, image);
                    if (active !== view || view.closed) return '';
                    return await new Promise((resolve) => {
                        const reader = new global.FileReader();
                        reader.onload = () => resolve(String(reader.result || ''));
                        reader.onerror = () => resolve('');
                        reader.readAsDataURL(blob);
                    });
                } catch {
                    return '';
                }
            }
            let canvas = null;
            try {
                canvas = await view.adapter.render(1, maxWidth);
                return canvas && active === view && !view.closed ? canvas.toDataURL('image/jpeg', quality) : '';
            } catch {
                return '';
            } finally {
                if (canvas) canvas.width = canvas.height = 1;
            }
        },
        current: () => (active ? {
            id: active.id,
            name: active.source.name,
            kind: active.source.kind,
            page: active.displayedPage || active.page,
            count: active.adapter?.count || 0,
            zoom: active.zoom,
            originSurface: active.origin.surface,
            comparison: active.comparison && !active.comparison.closed
                ? { name: active.comparison.source.name, page: active.comparison.displayedPage || active.comparison.page } : null
        } : null)
    });
}(window));
